import React, { useRef, useState, useCallback } from "react";
import { gallery } from "../mock";
import { MoveHorizontal } from "lucide-react";

function Slider({ item }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  const sliderId = item.title.toLowerCase().replace(/[^a-z0-9]+/gi, "-").replace(/(^-|-$)/g, "");

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  return (
    <div className="reveal">
      <div
        ref={ref}
        data-testid={`before-after-slider-${sliderId}`}
        className="relative h-[300px] sm:h-[420px] w-full overflow-hidden rounded-3xl ring-1 ring-slate-200 select-none cursor-ew-resize shadow-xl"
        onMouseDown={() => (dragging.current = true)}
        onMouseUp={() => (dragging.current = false)}
        onMouseLeave={() => (dragging.current = false)}
        onMouseMove={(e) => dragging.current && move(e.clientX)}
        onTouchMove={(e) => move(e.touches[0].clientX)}
      >
        <img src={item.after} alt="Efter" data-testid={`before-after-after-image-${sliderId}`} className="absolute inset-0 h-full w-full object-cover object-center" draggable={false} />
        <img src={item.before} alt="Före" data-testid={`before-after-before-image-${sliderId}`} className="absolute inset-0 h-full w-full object-cover object-center" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} draggable={false} />
        <span className="absolute top-4 left-4 rounded-full bg-slate-900/80 px-3 py-1 text-xs font-bold text-white" data-testid={`before-after-before-label-${sliderId}`}>FÖRE</span>
        <span className="absolute top-4 right-4 rounded-full bg-[#1B45C4] px-3 py-1 text-xs font-bold text-white" data-testid={`before-after-after-label-${sliderId}`}>EFTER</span>
        <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
          <div className="h-full w-1 bg-white" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-lg">
            <MoveHorizontal className="w-5 h-5 text-[#1B45C4]" />
          </div>
        </div>
      </div>
      <p className="mt-4 text-center font-semibold text-slate-800">{item.title}</p>
      {(item.ort || item.taktyp || item.tjanst || item.storlek || item.tid) && (
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          {[item.ort, item.taktyp, item.storlek, item.tjanst, item.tid].filter(Boolean).map((m, i) => (
            <span key={i} className="rounded-full bg-[#1B45C4]/[0.06] px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-[#dbeafe]">{m}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function StaticSplit({ item }) {
  return (
    <div className="reveal">
      <div className="relative h-[300px] sm:h-[420px] w-full overflow-hidden rounded-3xl ring-1 ring-slate-200 select-none shadow-xl" data-testid="ba-static-split">
        <img src={item.image} alt={item.title} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
        <span className="absolute top-4 left-4 rounded-full bg-slate-900/80 px-3 py-1 text-xs font-bold text-white">FÖRE</span>
        <span className="absolute top-4 right-4 rounded-full bg-[#1B45C4] px-3 py-1 text-xs font-bold text-white">EFTER</span>
        <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2">
          <div className="h-full w-1 bg-white/80" />
        </div>
      </div>
      <p className="mt-4 text-center font-semibold text-slate-800">{item.title}</p>
    </div>
  );
}

export default function BeforeAfter({ items, id = "galleri", eyebrow = "Före & efter", title = "Se skillnaden själv", subtitle = "Dra i reglaget för att se förvandlingen på riktiga HPR-projekt.", className = "hpr-blue-tint" }) {
  const list = items || gallery;
  return (
    <section id={id} className={`${className} py-16 lg:py-24`}>
      <div className="mx-auto max-w-7xl px-6">
        <div className="reveal text-center max-w-2xl mx-auto">
          <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#1B45C4]">{eyebrow}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900">{title}</h2>
          <p className="mt-4 text-slate-600 text-lg">{subtitle}</p>
        </div>
        <div className={`mt-12 grid gap-8 ${list.length > 1 ? "md:grid-cols-2" : "max-w-3xl mx-auto"}`}>
          {list.map((g) => g.split ? <StaticSplit key={g.title} item={g} /> : <Slider key={g.title} item={g} />)}
        </div>
      </div>
    </section>
  );
}
